<template>
    <b-container fluid>
        <b-row>
            <b-col cols="12">
                <admin-page-header title="벤더코드 관리" description="평가에 사용되는 벤더코드를 조회하고 일괄 등록합니다."/>
            </b-col>
        </b-row>

        <!-- 검색 필터 -->
        <b-row class="mb-3">
            <b-col cols="12">
                <b-card no-body bg-variant="white">
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <h5 class="mb-0">벤더코드 검색</h5>
                            <div v-if="listLoading" class="d-flex align-items-center" style="height: 36px">
                                <b-spinner class="mr-2" variant="primary" style="height: 1rem; width: 1rem;"/>
                                <p class="d-inline-block mb-0">조회중...</p>
                            </div>
                            <div v-else class="d-flex align-items-center">
                                <b-button form="vendor-code-search-form" type="reset" class="mr-2" :disabled="listLoading">
                                    초기화
                                </b-button>
                                <b-button variant="primary" :disabled="listLoading" form="vendor-code-search-form" type="submit">
                                    검색하기
                                </b-button>
                            </div>
                        </div>
                    </template>
                    <div class="p-3">
                        <b-form id="vendor-code-search-form" @submit.stop.prevent="onSearch" @reset="resetFilter">
                            <b-row>
                                <b-col cols="12" md="3" class="mb-3 mb-md-0">
                                    <b-form-group label="프로젝트 코드" label-for="filter-project-code" class="mb-0">
                                        <b-input-group size="sm">
                                            <b-form-input id="filter-project-code" v-model="filter.project_code" placeholder="프로젝트 코드 입력"/>
                                            <b-input-group-append>
                                                <b-button variant="outline-secondary" title="프로젝트 코드 검색" @click="openProjectCodeLookup">
                                                    <fa icon="search"/>
                                                </b-button>
                                            </b-input-group-append>
                                        </b-input-group>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="3" class="mb-3 mb-md-0">
                                    <b-form-group label="업체명" label-for="filter-company-name" class="mb-0">
                                        <b-form-input id="filter-company-name" v-model="filter.company_name" placeholder="업체명 입력" size="sm"/>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="3" class="mb-3 mb-md-0">
                                    <b-form-group label="벤더코드" label-for="filter-vendor-code" class="mb-0">
                                        <b-form-input id="filter-vendor-code" v-model="filter.vendor_code" placeholder="벤더코드 입력" size="sm"/>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="3">
                                    <b-form-group label="검증 여부" label-for="filter-validated" class="mb-0">
                                        <b-form-select id="filter-validated" v-model="filter.validated" :options="validatedOptions" size="sm"/>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </b-form>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <!-- 목록 테이블 -->
        <b-row>
            <b-col cols="12">
                <b-card no-body>
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <h5 class="mb-0">
                                벤더코드 목록
                                <b-badge variant="secondary" class="ml-1">{{ totalRows }}</b-badge>
                            </h5>
                            <div class="d-flex align-items-center">
                                <b-button size="sm" variant="outline-secondary" class="mr-2" :disabled="listLoading || downloading || items.length === 0" @click="downloadAsExcel">
                                    <b-spinner v-if="downloading" small class="mr-1"/>
                                    <fa v-else icon="download" class="mr-1"/>엑셀 다운로드 (전체 {{ totalRows }}건)
                                </b-button>
                                <b-button size="sm" variant="primary" @click="openBulkUploadModal">
                                    <fa icon="upload" class="mr-1"/>벌크 등록
                                </b-button>
                            </div>
                        </div>
                    </template>
                    <!-- DS 표 규칙: 행 44 고정 — 칸마다 한 줄, 넘치면 줄이고 전체는 title. 폭은 머리가 정한다 -->
                    <b-table
                        class="mb-0 sticky-header-sort-fix"
                        table-class="ds-table ds-table-fixed vendor-code-table"
                        :items="items"
                        :fields="fields"
                        :busy="listLoading"
                        no-local-sorting
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="sortDesc"
                        show-empty
                        hover
                        responsive="sm"
                        sticky-header="550px"
                        no-border-collapse
                        :empty-html="emptyHtml"
                        @sort-changed="onSortChanged"
                    >
                        <template #table-busy>
                            <div class="d-flex align-items-center justify-content-center my-3">
                                <b-spinner class="mr-2" variant="primary"/>
                                <span>로딩중...</span>
                            </div>
                        </template>
                        <!-- 번호는 표시 순서(페이지를 넘어 이어짐) — 식별자가 아니다 -->
                        <template #cell(index)="{ index }">
                            {{ (currentPage - 1) * perPage + index + 1 }}
                        </template>
                        <template #cell(vendor_code)="{ value }">
                            <span class="ds-ellipsis" :title="value">{{ value }}</span>
                        </template>
                        <!-- 메인 칸 = 기업 상세 링크(응답 company 에 id 가 있을 때만) -->
                        <template #cell(company)="{ value }">
                            <router-link v-if="value && value.id" :to="`/admin/company/${value.id}`" class="ds-detail-link ds-ellipsis" :title="value.company_name">{{ value.company_name || '-' }}</router-link>
                            <span v-else class="ds-ellipsis" :title="value && value.company_name">{{ value && value.company_name || '-' }}</span>
                        </template>
                        <template #cell(project_code)="{ value }">
                            <span class="ds-ellipsis" :title="value">{{ value }}</span>
                        </template>
                        <template #cell(campaign_name)="{ value }">
                            <span class="ds-ellipsis" :title="value">{{ value }}</span>
                        </template>
                        <template #cell(hd_corporate)="{ value }">
                            <span class="ds-ellipsis" :title="value">{{ value }}</span>
                        </template>
                        <template #cell(validated)="{ value }">
                            <span class="ds-status" :class="value ? 'ds-status-success' : 'ds-status-light'">{{ value ? '검증됨' : '미검증' }}</span>
                        </template>
                        <template #cell(validated_at)="{ value }">
                            {{ value ? value.slice(0, 10) : '-' }}
                        </template>
                    </b-table>
                    <div v-if="totalRows > perPage" class="d-flex justify-content-center py-3 border-top">
                        <b-pagination
                            v-model="currentPage"
                            :total-rows="totalRows"
                            :per-page="perPage"
                            :disabled="listLoading"
                            align="center"
                            class="mb-0"
                            @change="onPageChange"
                        />
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <!-- 벌크 등록 모달 -->
        <VendorCodeBulkUploadModal modal-id="bulk-upload-modal" @submitted="onBulkSubmitted"/>

        <!-- 프로젝트 코드 검색 모달 -->
        <ProjectCodeLookupModal modal-id="project-code-lookup-modal" @selected="onProjectCodeSelected"/>
    </b-container>
</template>

<script>
import { saveAs } from 'file-saver';
import VendorCodeBulkUploadModal from './VendorCodeBulkUploadModal.vue';
import ProjectCodeLookupModal from './ProjectCodeLookupModal.vue';

// 서버가 받는 정렬 키 (URL 의 sort_by 화이트리스트)
const SORTABLE_KEYS = ['vendor_code', 'project_code', 'validated', 'validated_at'];

// URL 에 적용된 검색 조건. 입력창(filter)에는 아직 검색하지 않은 값이 있을 수 있어 조회·다운로드는 이것만 쓴다
const filterFromQuery = (q) => ({
    project_code: q.project_code || '',
    company_name: q.company_name || '',
    vendor_code: q.vendor_code || '',
    validated: q.validated === 'true' ? true : (q.validated === 'false' ? false : null),
});
const filterParams = (f) => {
    const params = {};
    if (f.project_code) params.project_code = f.project_code;
    if (f.company_name) params.company_name = f.company_name;
    if (f.vendor_code) params.vendor_code = f.vendor_code;
    if (f.validated !== null) params.validated = f.validated;
    return params;
};

export default {
    name: 'VendorCodeManagement',
    components: { VendorCodeBulkUploadModal, ProjectCodeLookupModal },
    data() {
        return {
            // 조건 없이 들어온 초기 화면과 검색 결과 0건을 구분한다
            searched: false,
            filter: {
                project_code: '',
                company_name: '',
                vendor_code: '',
                validated: null,
            },
            validatedOptions: [
                { value: null, text: '전체' },
                { value: true, text: '검증됨' },
                { value: false, text: '미검증' },
            ],
            items: [],
            listLoading: false,
            downloading: false,
            // 서버 페이지네이션/정렬 상태
            currentPage: 1,
            perPage: 50,
            totalRows: 0,
            sortBy: null,
            sortDesc: false,
            // DS 표 규칙(번호 → 데이터, 번호는 정렬 없음). 폭을 안 적은 업체명 · 캠페인이 남는 폭을 나눠 갖는다.
            // 검증 · 검증일은 각각 서버 정렬 키(SORTABLE_KEYS)라 한 칸으로 합치지 않는다
            fields: [
                { key: 'index', label: 'No.', thClass: 'ds-cell-no', tdClass: 'ds-cell-no', thStyle: { width: '64px' } },
                { key: 'vendor_code', label: '벤더코드', sortable: true, thStyle: { width: '160px' } },
                { key: 'company', label: '업체명' },
                { key: 'project_code', label: '프로젝트 코드', sortable: true, thStyle: { width: '170px' } },
                { key: 'campaign_name', label: '캠페인' },
                { key: 'hd_corporate', label: 'HD법인', thStyle: { width: '120px' } },
                { key: 'validated', label: '검증', sortable: true, thStyle: { width: '104px' } },
                { key: 'validated_at', label: '검증일', sortable: true, tdClass: 'ds-cell-nowrap', thStyle: { width: '108px' } },
            ],
        };
    },
    computed: {
        emptyHtml() {
            return this.searched
                ? "<h6 class='mt-3 gray-400'>조회된 벤더코드가 없습니다.</h6>"
                : "<h6 class='mt-3 gray-400'>검색 조건을 입력하고 검색하세요.</h6>";
        },
    },
    watch: {
        '$route.query'() {
            this.syncFromRoute();
        },
    },
    mounted() {
        this.syncFromRoute();
    },
    methods: {
        // URL query <-> 검색 조건/페이지/정렬. 검색·페이지는 push, 정렬은 replace
        applyQueryToState() {
            const q = this.$route.query;
            this.filter = filterFromQuery(q);
            this.currentPage = Math.max(1, parseInt(q.current_page, 10) || 1);
            this.sortBy = SORTABLE_KEYS.includes(q.sort_by) ? q.sort_by : null;
            this.sortDesc = this.sortBy ? q.sort_dir === 'desc' : false;
        },
        // 검색 버튼 전용: 입력창 값으로 새 조건을 만든다 (조건이 없으면 전체 조회를 ?all=1 로 남겨 새로고침해도 유지)
        buildQuery() {
            const query = Object.entries(filterParams(this.filter))
                .reduce((acc, [key, value]) => ({...acc, [key]: String(value)}), {});
            if (Object.keys(query).length === 0) query.all = '1';
            if (this.sortBy) {
                query.sort_by = this.sortBy;
                query.sort_dir = this.sortDesc ? 'desc' : 'asc';
            }
            return query;
        },
        // query 가 완전히 비었을 때만 '검색 전'. all=1·정렬 등 키가 하나라도 있으면 조회한다
        syncFromRoute() {
            this.applyQueryToState();
            if (Object.keys(this.$route.query).length > 0) {
                this.fetchList();
            } else {
                this.items = [];
                this.totalRows = 0;
                this.searched = false;
            }
        },
        navigateQuery(query, replace = false) {
            const current = this.$route.query;
            const keys = Object.keys(query);
            if (keys.length === Object.keys(current).length && keys.every(key => String(query[key]) === String(current[key]))) {
                // 같은 조건으로 다시 검색: URL 이 안 바뀌어 watch 가 안 돌므로 직접 조회
                this.fetchList();
                return;
            }
            const navigate = replace ? this.$router.replace : this.$router.push;
            navigate.call(this.$router, { query }).catch(() => {});
        },
        async fetchList() {
            this.listLoading = true;
            try {
                const params = {
                    page: this.currentPage,
                    per_page: this.perPage,
                    ...filterParams(filterFromQuery(this.$route.query)),
                };
                if (this.sortBy) {
                    params.sort_by = this.sortBy;
                    params.sort_dir = this.sortDesc ? 'desc' : 'asc';
                }

                const { data } = await this.$axios.get('/vendor_codes', { params });
                this.items = data.items || [];
                this.totalRows = data.count || 0;
                this.searched = true;
            } catch (e) {
                if (e?.response?.status === 401) return;
                this.$EventBus.$emit('toast-failure', '벤더코드 조회', '조회 중 오류가 발생했습니다.');
            } finally {
                this.listLoading = false;
            }
        },
        // 검색 버튼: 항상 첫 페이지부터 조회
        onSearch() {
            this.navigateQuery(this.buildQuery());
        },
        // 페이지 이동 (b-pagination) — 적용된 조건은 그대로, 페이지만 바꾼다
        onPageChange(page) {
            const query = { ...this.$route.query };
            delete query.current_page;
            if (page > 1) query.current_page = String(page);
            this.navigateQuery(query);
        },
        // 서버 정렬 (컬럼 헤더 클릭) — 적용된 조건은 그대로, 정렬을 바꾸고 첫 페이지부터
        onSortChanged(ctx) {
            const query = { ...this.$route.query };
            ['current_page', 'sort_by', 'sort_dir'].forEach(key => delete query[key]);
            if (ctx.sortBy) {
                query.sort_by = ctx.sortBy;
                query.sort_dir = ctx.sortDesc ? 'desc' : 'asc';
            }
            this.navigateQuery(query, true);
        },
        // 초기화: 빈 query(검색 전)로 이동
        resetFilter() {
            this.filter = filterFromQuery({});
            if (Object.keys(this.$route.query).length === 0) {
                this.syncFromRoute();
                return;
            }
            this.$router.push({ query: {} }).catch(() => {});
        },
        // 화면에 적용된 조건(URL)으로 받는다. 새 탭(window.open)은 인증 헤더가 안 실리고 실패해도 알 수 없어 axios 로 받는다
        async downloadAsExcel() {
            this.downloading = true;
            try {
                const { data } = await this.$axios.get('/vendor_codes', {
                    params: { ...filterParams(filterFromQuery(this.$route.query)), as_file: true },
                    responseType: 'blob',
                });
                saveAs(data, 'vendor_codes.xlsx');
            } catch (e) {
                console.error(e); // eslint-disable-line no-console
                if (e?.response?.status === 401) return;
                this.$EventBus.$emit('toast-failure', '엑셀 다운로드', '벤더코드 엑셀을 내려받지 못했습니다. 잠시 후 다시 시도해주세요.');
            } finally {
                this.downloading = false;
            }
        },
        openBulkUploadModal() {
            this.$bvModal.show('bulk-upload-modal');
        },
        async onBulkSubmitted() {
            await this.fetchList();
        },
        openProjectCodeLookup() {
            this.$bvModal.show('project-code-lookup-modal');
        },
        onProjectCodeSelected({ project_code }) {
            this.filter.project_code = project_code;
        },
    },
};
</script>

<style>
/* 고정 열 합 726(No. 64 · 벤더코드 160 · 프로젝트 코드 170 · HD법인 120 · 검증 104 · 검증일 108) + 업체명 · 캠페인 ~157씩.
   1366(메뉴 펼침) 본문 폭 약 1074 안에 들어가게 — 그보다 좁으면 가로 스크롤 */
.vendor-code-table {
    min-width: 1040px;
}
</style>
